import { Popover, Row, Button, TextInput } from "@narmi/design_system";
import styles from "./FilterTag.module.css";
import { PropsWithChildren, useState, useRef, useCallback } from "react";

type FilterItemBaseProps = {
  label: string;
  width?: number;
  // To surface the count of applied checkboxes for list filters
  countApplied?: number;
  isExpanded: boolean;
  onPopoverDismiss: () => void;
  onToggle: () => void;
};

type FilterItemProps =
  // To allow custom forms to be passed in
  | (FilterItemBaseProps & {
      onApply?: never;
      onClear?: never;
      children: React.ReactNode;
    })
  // To allow the default form with the clear and apply buttons to be used
  | (FilterItemBaseProps & {
      onApply: () => void;
      onClear: () => void;
      children?: React.ReactNode;
    });

// A tag that opens a popover with a list of fields to filter on
export function FilterSelector({
  children,
  isExpanded,
  onToggle,
  onUserDismiss,
}: React.PropsWithChildren<{
  isExpanded: boolean;
  onToggle: () => void;
  onUserDismiss: () => void;
}>) {
  return (
    <Popover isOpen={isExpanded} onUserDismiss={onUserDismiss} content={children}>
      <div
        className={`nds-tag nds-tag--outline ${styles.tag} padding--x--xs fontSize--xs`}
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyUp={({ key }) => {
          if (key === "Enter" || key === " ") {
            onToggle();
          }
        }}
      >
        <i className="narmi-icon-filter-2 fontSize--m" />
      </div>
    </Popover>
  );
}

// A field that can be selected to filter on
// This is intended to spawn a "FilterItem" when clicked
function FilterSelectorItem({
  label,
  isSelected,
  onSelect,
}: {
  label: string;
  isSelected: boolean;
  onSelect: () => void;
}) {
  return (
    <div className={styles.selectorItem} onClick={onSelect}>
      <div className="padding--x--xs">{label}</div>
      {isSelected ? <i className="narmi-icon-check fontSize--m padding--x--xs" /> : null}
    </div>
  );
}

// A tag that can be clicked to expand a popover with filter options
export function FilterItem({
  children,
  label,
  width,
  countApplied,
  isExpanded,
  onPopoverDismiss,
  // When user clicks on the tag
  onToggle,
}: FilterItemProps) {
  return (
    <Popover
      content={<div style={{ width }}>{children}</div>}
      isOpen={isExpanded}
      onUserDismiss={onPopoverDismiss}
    >
      <div
        className={`nds-tag nds-tag--outline ${styles.tag} padding--x--xs fontSize--xs bgColor--white`}
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyUp={({ key }) => {
          if (key === "Enter" || key === " ") {
            onToggle();
          }
        }}
      >
        <div className="whiteSpace--truncate margin--right--xs">{label}</div>
        {countApplied ? <div className={styles.countContainer}>{countApplied}</div> : null}
        <div
          className={`${
            isExpanded ? "narmi-icon-chevron-up" : "narmi-icon-chevron-down"
          } fontSize--m margin--left--xxs`}
        />
      </div>
    </Popover>
  );
}

// We only allow either onSearchChange and onDebouncedSearch to be passed in
// We do not allow both to be passed in at the same time.
type onUndebouncedOrDebouncedSearch =
  | {
      onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
      onDebouncedSearch?: (debouncedValue: string) => void;
    }
  | {
      onSearchChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
      onDebouncedSearch: (debouncedValue: string) => void;
    };

function FilterSearch({
  searchValue,
  onSearchChange,
  onDebouncedSearch,
}: {
  searchValue: string;
} & onUndebouncedOrDebouncedSearch) {
  const [search, setSearch] = useState(searchValue);
  const timeout = useRef<NodeJS.Timeout | null>(null);

  const handleDebouncedChange = useCallback(
    (value: string) => {
      if (onDebouncedSearch) {
        clearTimeout(timeout.current!);
        timeout.current = setTimeout(() => {
          onDebouncedSearch(value.trim());
        }, 500); // Debounce delay of 500ms
      }
    },
    [onDebouncedSearch]
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearch(value);

    // Call the immediate change handler if provided
    if (onSearchChange) {
      onSearchChange(e);
    }

    // Call the debounced handler if provided
    if (onDebouncedSearch) {
      handleDebouncedChange(value);
    }
  };

  return (
    <div className="border--bottom padding--y--m padding--x--m">
      <TextInput label="Search" value={search} onChange={handleChange} autoFocus />
    </div>
  );
}

function FilterNoResultsFound() {
  return <div className="fontFamily--body">No results found.</div>;
}

function FilterActionBar({ onClear, onApply }: { onClear: () => void; onApply: () => void }) {
  return (
    <Row
      gapSize="m"
      className="padding--y--s padding--x--l"
      alignItems="center"
      justifyContent="space-between"
    >
      <Row.Item shrink>
        <Button label="Clear filter" size="s" kind="negative" onClick={onClear} />
      </Row.Item>
      <Row.Item shrink>
        <div className="padding--top--xxs">
          <Button label="Apply" size="s" onClick={onApply} />
        </div>
      </Row.Item>
    </Row>
  );
}

function FilterFormContainer({ children }: PropsWithChildren) {
  return (
    <div className="border--bottom">
      <div className={styles.contents}>{children}</div>
    </div>
  );
}

export function FilterComponent({ children }: React.PropsWithChildren) {
  return <Row>{children}</Row>;
}

// @deprecated
// TODO
// 1. Replace references to the new FilterItem instead
// 2. Change FilterItem name back to FilterTag
export function FilterTag({
  label,
  children,
  countApplied,
  isExpanded,
  setIsExpanded,
}: React.PropsWithChildren<{
  label: string;
  countApplied?: number;
  isExpanded: boolean;
  setIsExpanded: (isExpanded: boolean) => void;
}>) {
  return (
    <Popover content={children} isOpen={isExpanded} onUserDismiss={() => setIsExpanded(false)}>
      <div
        className={`nds-tag nds-tag--outline ${styles.tag} padding--x--xs fontSize--xs margin--right--l margin--top--l`}
        role="button"
        tabIndex={0}
        onClick={() => setIsExpanded(!isExpanded)}
        onKeyUp={({ key }) => {
          if (key === "Enter" || key === " ") {
            setIsExpanded(!isExpanded);
          }
        }}
      >
        <div className={`whiteSpace--truncate${countApplied ? " margin--right--xs" : ""}`}>
          {label}
        </div>
        {countApplied ? <div className={styles.countContainer}>{countApplied}</div> : null}
        <div
          className={`${
            isExpanded ? "narmi-icon-chevron-up" : "narmi-icon-chevron-down"
          } fontSize--m margin--left--xxs`}
        />
      </div>
    </Popover>
  );
}

FilterSelector.Item = FilterSelectorItem;
FilterComponent.Selector = FilterSelector;
FilterComponent.Tag = FilterItem;
FilterComponent.Search = FilterSearch;
FilterComponent.ActionBar = FilterActionBar;
FilterComponent.FormContainer = FilterFormContainer;
FilterComponent.NoResultsFound = FilterNoResultsFound;

export const FilterRow = FilterComponent;
